import { IconChevronLeft16 } from '@warp-ds/icons/react';
import { Alert, Button, Select, TextArea, TextField } from '@warp-ds/react';
import { Toggle } from '@warp-ds/react/components/toggle';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { type FormEvent, useEffect, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { monthNames } from 'shared/util/calendar';
import { sortedUniqueCountryCodes } from 'shared/util/countryCodes';
import { formatISODate, isBefore } from 'shared/util/datetime-fns';
import { formatMoney } from 'shared/util/formatting';
import { queryString } from 'shared/util/query';
import { createRentalRequest } from '@/api/transactionApi';
import { SafetyInfo } from '@/components/safetyInfo/SafetyInfo';
import { TJVR_RULES_ON_HELPCENTER } from '@/constants';
import type { PriceCleaningChoice } from '@/domain';
import type { User } from '@/domain/userLookup';
import { useObject } from '@/hooks/useObject';
import { usePrivateItemPrice } from '@/hooks/usePrivateItemPrice';
import {
    dateOrNull,
    invalidBirthDate,
    invalidMessageField,
    invalidNameField,
    invalidPhone,
    isValidDate,
    tooYoung,
} from '@/lib/order/validation';
import { trackCreateRentalRequestClick } from '@/lib/tracking/tjvrEventTracking';
import { trackRentalRequestPageView } from '@/lib/tracking/tjvrPageTracking';
import { RentalRequestItemOverview } from '@/scenes/createRentalRequestPage/components/RentalRequestItemOverview';
import { navigateToUrl } from '@/scenes/createRentalRequestPage/utils/navigation';
import styles from './createRentalRequestPage.module.css';
import { getLastRequestPersonalInfo, storeLastRequestPersonalInfo } from './utils/lastRequestPersonalInfo';

const currentYear = new Date().getFullYear();

const splitPhoneNumber = (phoneNumber?: string | null): { basePhoneNumber: string; landCode: string } | null => {
    if (!phoneNumber) return null;

    const landCode = sortedUniqueCountryCodes.filter((cc) => Boolean(cc)).find((cc) => phoneNumber.startsWith(`+${cc}`));
    return landCode
        ? {
              basePhoneNumber: phoneNumber.slice(`+${landCode}`.length),
              landCode: `+${landCode}`,
          }
        : null;
};

export type CreateRentalRequestPageProps = {
    user: User | null;
};

const CreateRentalRequestPage = ({ user }: CreateRentalRequestPageProps) => {
    const objectData = useObject();
    const { baseUrl, contextPath, transactionExternalUrl } = useConfig();

    const { adId, images } = objectData;
    const router = useRouter();
    const { startDate, endDate, guests } = router?.query || {};
    const currentDate = new Date();

    const startDateFormatted = new Date(startDate as string);
    const endDateFormatted = new Date(endDate as string);

    const [shouldValidateForm, setShouldValidateForm] = useState(false);
    const [firstname, setFirstname] = useState(user?.firstName ?? '');
    const [surname, setSurname] = useState(user?.surname ?? '');
    const [dateOfBirthDay, setDateOfBirthDay] = useState<string>('');
    // dateOfBirthMonth is 0-indexed as javascripts date object -_-
    const [dateOfBirthMonth, setDateOfBirthMonth] = useState<string>('');
    const [dateOfBirthYear, setDateOfBirthYear] = useState<string>(user?.birthYear?.toString() ?? '');

    const phoneData = splitPhoneNumber(user?.phone);
    const [landcode, setLandCode] = useState(phoneData?.landCode ?? '+47');
    const [phone, setPhone] = useState(phoneData?.basePhoneNumber ?? '');
    const [message, setMessage] = useState('');
    const [cleaningChoice, setCleaningChoice] = useState<PriceCleaningChoice | null>();
    const missingUrlParams = !startDate || !endDate || !guests;
    const validUrlDatesAndGuests = isValidDate(startDateFormatted) && isValidDate(endDateFormatted) && !Number.isNaN(Number(guests));
    const rentalPeriodHasPassed = isBefore(currentDate, endDateFormatted) || isBefore(currentDate, startDateFormatted);
    const [requestFailed, setRequestFailed] = useState(false);
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);
    const targetYear = dateOfBirthYear ? Number(dateOfBirthYear) : new Date().getFullYear();
    const maxDaysInMonth = new Date(targetYear, (Number(dateOfBirthMonth) || 0) + 1, 0).getDate();

    const { privateItemPrice } = usePrivateItemPrice(
        adId.toString(),
        isValidDate(startDateFormatted) ? startDateFormatted : new Date(),
        isValidDate(endDateFormatted) ? endDateFormatted : new Date(),
        Number.isNaN(Number(guests)) ? 1 : Number(guests),
        cleaningChoice,
    );

    useEffect(() => {
        trackRentalRequestPageView(adId, objectData.user?.isOwner || false);
        if (!missingUrlParams && !rentalPeriodHasPassed && validUrlDatesAndGuests) {
            updateValuesFromLastRequest();
        }
    }, []);

    const updateValuesFromLastRequest = () => {
        const lastRequest = getLastRequestPersonalInfo();
        if (lastRequest?.firstName) {
            setFirstname(lastRequest.firstName);
        }
        if (lastRequest?.lastName) {
            setSurname(lastRequest.lastName);
        }
        if (lastRequest?.dateOfBirth) {
            setDateOfBirthDay(lastRequest.dateOfBirth.getDate().toString());
            setDateOfBirthMonth(lastRequest.dateOfBirth.getMonth().toString());
            setDateOfBirthYear(lastRequest.dateOfBirth.getFullYear().toString());
        }
        if (lastRequest?.phoneCountryCode) {
            setLandCode(lastRequest.phoneCountryCode);
        }
        if (lastRequest?.phoneNumber) {
            setPhone(lastRequest.phoneNumber);
        }

        if (lastRequest?.messageFromTenant) {
            setMessage(lastRequest.messageFromTenant);
        }
    };

    const toggleCleaningChoice = (wantsToPayForCleaning: boolean) => {
        setCleaningChoice(wantsToPayForCleaning ? 'FEE' : 'GUEST_CLEANS');
    };

    const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setShouldValidateForm(true);
        if (anyInvalidFields()) {
            return;
        }
        setRequestFailed(false);
        setSubmitDisabled(true);

        const dateOfBirth = dateOrNull({ day: dateOfBirthDay, month: dateOfBirthMonth, year: dateOfBirthYear });
        if (!dateOfBirth) {
            return;
        }

        storeLastRequestPersonalInfo({
            firstName: firstname.trim(),
            lastName: surname.trim(),
            dateOfBirth,
            phoneCountryCode: landcode,
            phoneNumber: phone.trim(),
            messageFromTenant: user ? null : message.trim(),
        });

        if (user) {
            trackCreateRentalRequestClick(
                adId,
                startDate as string,
                endDate as string,
                Number(guests),
                privateItemPrice?.total,
                objectData.location?.country,
                objectData.location?.locality,
            );

            const requestValues = `itemId=${adId}&fromDate=${startDate}&toDate=${endDate}&guests=${guests}&firstName=${firstname.trim()}&lastName=${surname.trim()}&dateOfBirth=${formatISODate(
                dateOfBirth,
            )}&phoneCountryCode=${landcode}&phoneNumber=${phone.trim()}&message=${encodeURIComponent(message.trim())}${cleaningChoice ? `&cleaningChoice=${cleaningChoice}` : ''}`;

            createRentalRequest(transactionExternalUrl, requestValues).then((bookingReference) => {
                bookingReference ? router.push(`/leieforhold/${bookingReference}/leietaker/`) : handleRequestFailed();
            });
        } else {
            const redirectUrl = encodeURIComponent(
                `${baseUrl}${contextPath}/opprettforesporsel/${queryString({
                    finnkode: adId,
                    startDate: startDate,
                    endDate: endDate,
                    guests: guests,
                    ...(cleaningChoice ? { cleaningChoice: cleaningChoice } : {}),
                })}`,
            );
            navigateToUrl(`${baseUrl}/auth/login?redirectUrl=${redirectUrl}`);
        }
    };

    const handleRequestFailed = () => {
        setRequestFailed(true);
        setSubmitDisabled(false);
    };

    const anyInvalidFields = () =>
        invalidNameField(firstname) ||
        invalidNameField(surname) ||
        invalidMessageField(message) ||
        invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear) ||
        tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear) ||
        invalidPhone(landcode, phone);

    const renderCleaningOptions = () => {
        if (privateItemPrice?.cleaning?.cleaningOption === 'OPTIONAL_GUEST_CLEANS_OR_FEE') {
            return (
                <div className="flex flex-col gap-12 mt-8">
                    <h3 className="mb-0">Tilleggstjenester</h3>
                    <p>Totalprisen endrer seg når du legger til tjenester. Ta en siste titt før du sender forespørselen.</p>
                    <Toggle
                        type="checkbox"
                        label={`Jeg ønsker å betale for rengjøring (+ ${formatMoney(privateItemPrice.cleaning.amount, 0)})`}
                        checked={cleaningChoice === 'FEE'}
                        onChange={toggleCleaningChoice}
                        data-testid="rentalRequestCleaningService"
                    />
                </div>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col gap-24 my-24">
            <Button link className="flex flex-row gap-6 font-bold items-center mt-12" href={`${contextPath}/ad.html?finnkode=${adId}`}>
                <IconChevronLeft16 /> Tilbake til annonsen
            </Button>
            {missingUrlParams || rentalPeriodHasPassed || !validUrlDatesAndGuests ? (
                <Alert type="negative" show className="my-32">
                    <strong>Vi mangler gyldig data for ønsket leieperiode og/eller antall gjester</strong>
                    <p className="mt-8">Gå tilbake til annonsen og prøv igjen.</p>
                </Alert>
            ) : (
                <div className="flex flex-col items-center md:items-start md:justify-start gap-14">
                    <div className="flex flex-row flex-wrap gap-24 mt-8 md:flex-row-reverse md:flex-nowrap">
                        <div className="flex flex-col gap-12 md:w-1/2">
                            <RentalRequestItemOverview
                                itemId={adId}
                                image={images?.[0]}
                                startDate={new Date(startDate as string)}
                                endDate={new Date(endDate as string)}
                                numberOfGuests={guests}
                                price={privateItemPrice}
                            />
                            <SafetyInfo smallText={true} />
                        </div>
                        <form
                            className={`flex flex-col w-full md:max-w-full gap-14 ${styles.form}`}
                            onSubmit={handleSubmitForm}
                            data-testid="rentalRequestForm">
                            <header>
                                <h1 className="h3">Nå er du snart i mål</h1>
                                <p>
                                    Vi behøver bare litt mer informasjon før forespørselen kan sendes. Denne informasjonen deles også med
                                    utleier av feriehjemmet.
                                </p>
                            </header>
                            <div className="flex flex-col gap-14">
                                <div className="flex flex-row w-full gap-8 align-items-stretch">
                                    <TextField
                                        className="flex-1"
                                        name="firstName"
                                        label="Fornavn"
                                        value={firstname}
                                        type="text"
                                        onChange={(e) => setFirstname(e.target.value)}
                                        data-testid="rentalRequestFirstName"
                                        helpText={shouldValidateForm ? invalidNameField(firstname, 'Fornavn') : null}
                                        invalid={shouldValidateForm && Boolean(invalidNameField(firstname))}
                                    />
                                    <TextField
                                        className="flex-1"
                                        name="lastName"
                                        label="Etternavn"
                                        value={surname}
                                        onChange={(e) => setSurname(e.target.value)}
                                        data-testid="rentalRequestSurname"
                                        helpText={shouldValidateForm ? invalidNameField(surname, 'Etternavn') : null}
                                        invalid={shouldValidateForm && Boolean(invalidNameField(surname))}
                                    />
                                </div>
                                <fieldset className={`${styles.inputs}`}>
                                    <legend
                                        className={`text-14 font-bold mb-4 antialiased ${shouldValidateForm && (invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear) || tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)) ? 's-text-negative' : ''}`}>
                                        Fødselsdato
                                    </legend>
                                    <div className="flex flex-row justify-start gap-8">
                                        <Select
                                            className={styles.day}
                                            data-testid="rentalRequestDateOfBirthDay"
                                            name="dateOfBirthDay"
                                            value={dateOfBirthDay}
                                            aria-describedby="age-error"
                                            invalid={
                                                (shouldValidateForm && tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)) ||
                                                (shouldValidateForm && invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear))
                                            }
                                            onChange={(e) => setDateOfBirthDay(e.target.value)}>
                                            <option key="default" value="">
                                                Dag
                                            </option>
                                            {Array.from(Array(maxDaysInMonth).keys()).map((i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {i + 1}
                                                </option>
                                            ))}
                                        </Select>
                                        <Select
                                            className={styles.month}
                                            data-testid="rentalRequestDateOfBirthMonth"
                                            name="dateOfBirthMonth"
                                            value={dateOfBirthMonth}
                                            aria-describedby="age-error"
                                            invalid={
                                                (shouldValidateForm && tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)) ||
                                                (shouldValidateForm && invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear))
                                            }
                                            onChange={(e) => setDateOfBirthMonth(e.target.value)}>
                                            <option key="default" value="">
                                                Måned
                                            </option>
                                            {monthNames.map((monthName, i) => (
                                                <option key={monthName} value={i}>
                                                    {monthName}
                                                </option>
                                            ))}
                                        </Select>
                                        <Select
                                            className={styles.year}
                                            data-testid="rentalRequestDateOfBirthYear"
                                            name="dateOfBirthYear"
                                            value={dateOfBirthYear}
                                            aria-describedby="age-error"
                                            invalid={
                                                (shouldValidateForm && tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)) ||
                                                (shouldValidateForm && invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear))
                                            }
                                            onChange={(e) => setDateOfBirthYear(e.target.value)}>
                                            <option key="default" value="">
                                                År
                                            </option>
                                            {Array.from(Array(150).keys()).map((i) => (
                                                <option key={currentYear - i} value={currentYear - i}>
                                                    {currentYear - i}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>

                                    {shouldValidateForm && (
                                        <p id="age-error" aria-live="assertive" className="text-12 s-text-negative">
                                            {invalidBirthDate(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)
                                                ? 'Gyldig fødselsdato må fylles ut.'
                                                : null}
                                            {tooYoung(dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear)
                                                ? 'Du må være over 18 år for å sende en forespørsel.'
                                                : null}
                                        </p>
                                    )}
                                </fieldset>
                                <div>
                                    <legend
                                        className={`text-14 font-bold mb-4 antialiased ${shouldValidateForm && (invalidPhone(landcode, phone)) ? 's-text-negative' : ''}`}>
                                        Telefonnummer
                                    </legend>
                                    <div className={`${styles.inputs} flex flex-row justify-start gap-8`}>
                                        <Select
                                            className={styles.countryCode}
                                            data-testid="rentalRequestPhoneCountryCode"
                                            name="phoneCountryCode"
                                            value={landcode}
                                            onChange={(e) => setLandCode(e.target.value)}
                                            required>
                                            <option value="+47">+47</option>
                                            <optgroup />
                                            {sortedUniqueCountryCodes.map((cc) => (
                                                <option value={`+${cc}`} key={cc} data-testid={`cc-${cc}`}>
                                                    +{cc}
                                                </option>
                                            ))}
                                        </Select>
                                        <div className={styles.phoneNumber}>
                                            <TextField
                                                className="remove-scroll-wheel"
                                                name="phoneNumber"
                                                value={phone}
                                                type="number"
                                                pattern="\d*"
                                                onChange={(e) => setPhone(e.target.value)}
                                                data-testid="rentalRequestPhoneNumber"
                                                helpText={shouldValidateForm ? invalidPhone(landcode, phone) : null}
                                                invalid={shouldValidateForm && Boolean(invalidPhone(landcode, phone))}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <TextArea
                                name="message"
                                label="Melding til utleier"
                                placeholder="Beskriv deg og ditt reisefølge"
                                rows={5}
                                id="message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                data-testid="rentalRequestMessage"
                                className={cn('', styles.textArea)}
                                helpText={shouldValidateForm && invalidMessageField(message)}
                                invalid={shouldValidateForm && Boolean(invalidMessageField(message))}
                            />
                            {renderCleaningOptions()}
                            {shouldValidateForm && anyInvalidFields() && (
                                <Alert type="negative" show>
                                    <span data-testid="rentalRequestFormError">Sjekk at alle feltene er fylt ut riktig</span>
                                </Alert>
                            )}
                            {requestFailed && (
                                <Alert type="negative" show>
                                    Noe gikk galt ved sending av forespørsel
                                </Alert>
                            )}
                            <div className="my-24">
                                <Button
                                    primary
                                    type="submit"
                                    className={`${isSubmitDisabled ? 'in-progress' : ''}`}
                                    disabled={isSubmitDisabled}>
                                    {user ? 'Send forespørsel' : 'Logg inn for å sende forespørsel'}
                                </Button>
                                <p className="text-12 s-text-subtle mt-8 mb-6">
                                    <span className="mr-4">Ved å sende forspørselen godtar du</span>
                                    <Button
                                        link
                                        className="text-12 no-linebreak"
                                        href={TJVR_RULES_ON_HELPCENTER}
                                        target="_blank"
                                        rel="noreferrer">
                                        vilkår for tjenesten
                                    </Button>
                                    <span className="ml-4">
                                        og at du er over 18 år. Du betaler ingenting før utleier har godtatt forespørselen.
                                    </span>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
export default CreateRentalRequestPage;
